import type {ReactElement} from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE, PILL_THEME, PILL_TYPE} from '@Components/pill-legacy';
import {TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EditorViewport, EmailMakerSaveState} from '@EmailMakerReact/email-maker-react.types';
import {canCurrentUserAutosaveTemplate, hasEmailTemplateSavedOnce} from '@EmailMakerReact/lib/email-maker-storage-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {assertNever} from '@Utils/assert-never.util';
import {getRelativeTimeString} from '@Utils/relative-save-time.util';
import {ICON_SAVE, ICON_SAVE_STATE_OFFLINE, SAVE_TIME_UPDATE_INTERVAL_MS} from './save-status.types';
import type {SaveStatusProps} from './save-status.types';
import {useIsVersionHistoryAccessible} from '@EmailMakerReact/hooks/use-is-version-history-accessible';
import {SaveStatusBadge} from './components/save-status-badge/save-status-badge';
import {SaveStatusBadgeVariant} from './components/save-status-badge/save-status-badge.types';
import {SaveStatusVersionHistorySwap} from './components/save-status-version-history-swap/save-status-version-history-swap';
import {useAppDispatch} from '@/hooks';
import styles from './save-status.module.scss';

export function SaveStatus({onErrorIndicatorClick}: SaveStatusProps): ReactElement {
  const {editor, state, actions} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const {saveState, autosaveEnabled} = state;
  const isVersionHistoryAccessible = useIsVersionHistoryAccessible();
  const [savedAtMs, setSavedAtMs] = useState<number | null>(null);
  const [relativeTime, setRelativeTime] = useState<string>(window.i18next.t('pmwjs_saved_a_moment_ago'));

  const canAutosave = editor !== null && canCurrentUserAutosaveTemplate(editor);

  const prevSaveStateRef = useRef<EmailMakerSaveState>(saveState);
  useEffect(() => {
    if (saveState === EmailMakerSaveState.SAVED && prevSaveStateRef.current !== EmailMakerSaveState.SAVED) {
      const now = Date.now();
      setSavedAtMs(now);
      setRelativeTime(window.i18next.t('pmwjs_saved_a_moment_ago'));
    }
    prevSaveStateRef.current = saveState;
  }, [saveState]);

  useEffect(() => {
    if (saveState !== EmailMakerSaveState.SAVED || savedAtMs === null) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setRelativeTime(getRelativeTimeString(Date.now(), savedAtMs));
    }, SAVE_TIME_UPDATE_INTERVAL_MS);
    return (): void => {
      window.clearInterval(id);
    };
  }, [saveState, savedAtMs]);

  const isMobile = state.editorViewport === EditorViewport.MOBILE;

  function openVersionHistory(): void {
    if (state.editorViewport === EditorViewport.DESKTOP) {
      actions.openRightPanelVersionHistory();
      return;
    }
    dispatch(openPanelInModal({modalId: MODALS_IDS.EMAIL_MAKER_VERSION_HISTORY_SHEET, panelProps: {}}));
  }

  return (
    <div className={styles.container}>
      {renderBadge({saveState, relativeTime, savedAtMs, canAutosave, editor, isMobile, onErrorIndicatorClick, isVersionHistoryAccessible, onOpenVersionHistory: openVersionHistory})}
      {renderSaveButton(saveState, autosaveEnabled, actions.save, isMobile)}
    </div>
  );
}

interface RenderBadgeParams {
  saveState: EmailMakerSaveState;
  relativeTime: string;
  savedAtMs: number | null;
  canAutosave: boolean;
  editor: Parameters<typeof hasEmailTemplateSavedOnce>[0] | null;
  isMobile: boolean;
  onErrorIndicatorClick: () => void;
  isVersionHistoryAccessible: boolean;
  onOpenVersionHistory: () => void;
}

function renderBadge({
  saveState,
  relativeTime,
  savedAtMs,
  canAutosave,
  editor,
  isMobile,
  onErrorIndicatorClick,
  isVersionHistoryAccessible,
  onOpenVersionHistory,
}: RenderBadgeParams): ReactElement | null {
  const hasSavedOnce = editor !== null && hasEmailTemplateSavedOnce(editor);
  const isDisabled = (!isUserLoggedIn() || !canAutosave) && !hasSavedOnce;

  if (isMobile) {
    return renderMobileStateIcon(saveState, isDisabled, onErrorIndicatorClick, isVersionHistoryAccessible ? onOpenVersionHistory : undefined);
  }

  const {icon, label} = resolveSaveStateGlyph(saveState);
  switch (saveState) {
    case EmailMakerSaveState.SAVING:
      return <SaveStatusBadge icon={icon} label={label} variant={SaveStatusBadgeVariant.SAVING} />;
    case EmailMakerSaveState.NOT_SAVED:
      return renderErrorPill(label, onErrorIndicatorClick);
    case EmailMakerSaveState.SAVED:
      return renderIdleBadge({isDisabled, icon, label, savedAtMs, relativeTime, isVersionHistoryAccessible, onOpenVersionHistory});
    default:
      return assertNever(saveState);
  }
}

interface RenderIdleBadgeParams {
  isDisabled: boolean;
  icon: string;
  label: string;
  savedAtMs: number | null;
  relativeTime: string;
  isVersionHistoryAccessible: boolean;
  onOpenVersionHistory: () => void;
}

/**
 * The idle (SAVED) badge. When version history is reachable it becomes a hover entry point into the panel;
 * otherwise it shows the relative-save-time tooltip. The autosave-disabled indicator only applies when no
 * save is in flight; transient SAVING/NOT_SAVED states render their own badge instead.
 */
function renderIdleBadge({isDisabled, icon, label, savedAtMs, relativeTime, isVersionHistoryAccessible, onOpenVersionHistory}: RenderIdleBadgeParams): ReactElement {
  if (isDisabled) {
    return (
      <SaveStatusBadge
        icon={ICON_SAVE_STATE_OFFLINE}
        label={window.i18next.t('pmwjs_disabled')}
        variant={SaveStatusBadgeVariant.DISABLED}
        tooltipText={window.i18next.t('pmwjs_autosave_disabled_description')}
      />
    );
  }

  if (isVersionHistoryAccessible) {
    return <SaveStatusVersionHistorySwap savedIcon={icon} savedLabel={label} onOpenVersionHistory={onOpenVersionHistory} />;
  }

  // No tooltip on mount until a save happens this session (avoids misleading "Saved a moment ago").
  const tooltipText = savedAtMs !== null ? relativeTime : undefined;
  return <SaveStatusBadge icon={icon} label={label} tooltipText={tooltipText} />;
}

/** Persistent "Unable to save." indicator, surfaced as a danger pill that opens the save-error popover. */
function renderErrorPill(label: string, onClick: () => void): ReactElement {
  return (
    <Pill
      content={{type: PILL_CONTENT_TYPE.HYBRID, icon: ICON_SAVE_STATE_OFFLINE, iconSize: IconSize.SIZE_ICON_12, textSize: TextSize.SM, text: label}}
      theme={PILL_THEME.DANGER}
      type={PILL_TYPE.ROUND}
      size={PILL_SIZE.XSMALL}
      className={styles.errorPill}
      isHoverable
      onClick={onClick}
    />
  );
}

function resolveSaveStateGlyph(saveState: EmailMakerSaveState): {icon: string; label: string} {
  switch (saveState) {
    case EmailMakerSaveState.SAVING:
      return {icon: 'icon-save-loading', label: window.i18next.t('pmwjs_saving')};
    case EmailMakerSaveState.NOT_SAVED:
      return {icon: ICON_SAVE_STATE_OFFLINE, label: window.i18next.t('pmwjs_not_saved_retry')};
    case EmailMakerSaveState.SAVED:
      return {icon: 'icon-auto-saved', label: window.i18next.t('pmwjs_all_changes_saved_without_period')};
    default:
      return assertNever(saveState);
  }
}

/**
 * Icon-only state glyph for the mobile nav; aria-label carries the state since the text is dropped.
 * The idle SAVED icon opens the version-history sheet when onOpenVersionHistory is provided.
 */
function renderMobileStateIcon(saveState: EmailMakerSaveState, isDisabled: boolean, onErrorIndicatorClick: () => void, onOpenVersionHistory?: () => void): ReactElement {
  if (saveState === EmailMakerSaveState.NOT_SAVED) {
    const {icon, label} = resolveSaveStateGlyph(saveState);
    return (
      <span className={styles.mobileStateIcon}>
        <Icon icon={icon} isUsedAsButton onClick={onErrorIndicatorClick} type={IconType.NONE} shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_20} ariaLabel={label} />
      </span>
    );
  }

  const {icon, label} = resolveMobileIdleGlyph(saveState, isDisabled);
  const isVersionHistoryOpener = saveState === EmailMakerSaveState.SAVED && !isDisabled && onOpenVersionHistory !== undefined;
  return (
    <span className={styles.mobileStateIcon}>
      <Icon
        icon={icon}
        isUsedAsButton={isVersionHistoryOpener}
        onClick={isVersionHistoryOpener ? onOpenVersionHistory : undefined}
        type={IconType.NONE}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        ariaLabel={isVersionHistoryOpener ? window.i18next.t('pmwjs_view_version_history') : label}
      />
    </span>
  );
}

/** Mobile idle glyph: the autosave-disabled marker only applies to the idle SAVED state; SAVING shows the saving glyph. */
function resolveMobileIdleGlyph(saveState: EmailMakerSaveState, isDisabled: boolean): {icon: string; label: string} {
  if (saveState === EmailMakerSaveState.SAVED && isDisabled) {
    return {icon: ICON_SAVE_STATE_OFFLINE, label: window.i18next.t('pmwjs_disabled')};
  }
  return resolveSaveStateGlyph(saveState);
}

function renderSaveButton(saveState: EmailMakerSaveState, autosaveEnabled: boolean, onSave: () => Promise<boolean>, isMobile: boolean): ReactElement | null {
  if (saveState === EmailMakerSaveState.SAVED && autosaveEnabled) {
    return null;
  }

  if (saveState === EmailMakerSaveState.SAVING) {
    return null;
  }

  if (isMobile) {
    return renderMobileSaveButton(onSave);
  }

  return (
    <Button
      type={ButtonType.TERTIARY} onDark
      size={ButtonSize.MEDIUM}
      text={window.i18next.t('pmwjs_save')}
      icon={ICON_SAVE}
      iconPlacement={IconPlacement.LEFT}
      onClick={(): void => {
        void onSave();
      }}
    />
  );
}

function renderMobileSaveButton(onSave: () => Promise<boolean>): ReactElement {
  return (
    <Icon
      ariaLabel={window.i18next.t('pmwjs_save')}
      icon={ICON_SAVE}
      isUsedAsButton
      onClick={(): void => {
        void onSave();
      }}
      shape={IconShape.SQUARE}
      size={IconSize.SIZE_ICON_20}
      type={IconType.DARK_GHOST}
    />
  );
}
